import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, formatApiError, setStoredToken } from '../lib/api';
import { useAuth } from '../lib/AuthContext';
import { ArrowRight, KeyRound, Mail, Shield } from 'lucide-react';
import { Logo } from '../components/Logo';
import { PasswordStrength, isPasswordValid } from '../components/PasswordStrength';

export default function ForgotPassword() {
    const nav = useNavigate();
    const { verify2fa } = useAuth();
    // step 1 = email, 2 = code + new password, 3 = 2FA (only if account has it)
    const [step, setStep] = useState(1);
    const [email, setEmail] = useState('');
    const [code, setCode] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [confirmPassword, setConfirmPassword] = useState('');
    const [pendingToken, setPendingToken] = useState('');
    const [totpCode, setTotpCode] = useState('');
    const [error, setError] = useState('');
    const [info, setInfo] = useState('');
    const [loading, setLoading] = useState(false);

    const submitEmail = async (e) => {
        e.preventDefault();
        setError('');
        setInfo('');
        setLoading(true);
        try {
            const { data } = await api.post('/auth/forgot-password', { email: email.trim() });
            // Security: backend returns identical response whether the email is
            // registered or not. Show the same generic message to the user.
            setInfo(
                data.dev_code
                    ? `Cod dev: ${data.dev_code} (DEV mode — în producție vine doar pe email).`
                    : `Dacă există un cont cu adresa ${email.trim()}, ți-am trimis un cod de 6 cifre. Verifică inbox-ul (și folderul Spam).`
            );
            setStep(2);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const submitReset = async (e) => {
        e.preventDefault();
        setError('');
        if (!isPasswordValid(newPassword)) {
            setError('Parola nu îndeplinește toate cerințele de securitate.');
            return;
        }
        if (newPassword !== confirmPassword) {
            setError('Parolele nu coincid.');
            return;
        }
        setLoading(true);
        try {
            const { data } = await api.post('/auth/reset-password', {
                email: email.trim(),
                code: code.trim(),
                new_password: newPassword,
            });
            // If 2FA is enabled, backend asks for the second factor before
            // issuing a session. Switch to step 3.
            if (data.requires_2fa) {
                setPendingToken(data.pending_token);
                setTotpCode('');
                setStep(3);
                return;
            }
            if (data.access_token) setStoredToken(data.access_token);
            nav('/r', { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    const submit2fa = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const data = await verify2fa(pendingToken, totpCode);
            // verify2fa stores the access token + refreshes auth context.
            nav('/r', { replace: true });
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="grain" data-testid="forgot-password-page"
             style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem 1rem' }}>
            <div style={{ width: '100%', maxWidth: 440 }} className="fade-in-up">
                <div style={{ marginBottom: '2rem', display: 'flex', justifyContent: 'center' }}>
                    <Logo size="lg" />
                </div>

                {step === 1 && (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, justifyContent: 'center' }}>
                            <Mail size={22} color="var(--amber)" />
                            <h1 className="headline" style={{ fontSize: '2rem', margin: 0 }}>Am uitat parola</h1>
                        </div>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '1.4rem', textAlign: 'center' }}>
                            Introdu emailul contului tău. Dacă există un cont cu acea adresă, îți trimitem un cod de 6 cifre pentru a-ți seta o parolă nouă.
                        </p>
                        <form onSubmit={submitEmail} className="panel" style={{ padding: '1.6rem' }}>
                            <div style={{ marginBottom: '1rem' }}>
                                <label className="label">Email</label>
                                <input className="input" type="email" required autoFocus
                                       data-testid="forgot-email"
                                       value={email} onChange={(e) => setEmail(e.target.value)}
                                       placeholder="nume@domeniu.ro" />
                            </div>
                            {error && <div style={{ color: 'var(--neon-magenta)', fontSize: '0.88rem', marginBottom: '0.8rem' }}
                                          data-testid="forgot-error">{error}</div>}
                            <button type="submit" disabled={loading || !email}
                                    className="btn btn-primary" style={{ width: '100%' }}
                                    data-testid="forgot-submit">
                                {loading ? <span className="spinner" /> : <>Trimite codul <ArrowRight size={16} /></>}
                            </button>
                        </form>
                    </>
                )}

                {step === 2 && (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, justifyContent: 'center' }}>
                            <KeyRound size={22} color="var(--neon-teal)" />
                            <h1 className="headline" style={{ fontSize: '2rem', margin: 0 }}>Parolă nouă</h1>
                        </div>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '1.4rem', textAlign: 'center' }}>
                            Introdu codul primit pe email și parola nouă pentru <strong style={{ color: '#fff' }}>{email}</strong>.
                        </p>
                        {info && <div className="panel" style={{ background: 'rgba(20, 184, 166, 0.08)', borderColor: 'var(--neon-teal)', padding: '0.8rem', marginBottom: '1rem', fontSize: '0.85rem', color: 'var(--neon-teal)' }} data-testid="forgot-info">{info}</div>}
                        <form onSubmit={submitReset} className="panel" style={{ padding: '1.6rem' }}>
                            <div style={{ marginBottom: '1rem' }}>
                                <label className="label">Cod (6 cifre)</label>
                                <input className="input" inputMode="numeric" maxLength={6} required autoFocus
                                       data-testid="reset-code"
                                       value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                                       placeholder="000000"
                                       style={{ letterSpacing: '0.45em', textAlign: 'center', fontSize: '1.3rem' }} />
                            </div>
                            <div style={{ marginBottom: '1rem' }}>
                                <label className="label">Parolă nouă</label>
                                <input className="input" type="password" required
                                       data-testid="reset-new-password"
                                       value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                                       placeholder="••••••••" />
                                <PasswordStrength value={newPassword} />
                            </div>
                            <div style={{ marginBottom: '1rem' }}>
                                <label className="label">Confirmă parola</label>
                                <input className="input" type="password" required
                                       data-testid="reset-confirm-password"
                                       value={confirmPassword} onChange={(e) => setConfirmPassword(e.target.value)}
                                       placeholder="••••••••" />
                            </div>
                            {error && <div style={{ color: 'var(--neon-magenta)', fontSize: '0.88rem', marginBottom: '0.8rem' }}
                                          data-testid="reset-error">{error}</div>}
                            <button type="submit" disabled={loading || code.length < 6 || !isPasswordValid(newPassword) || !confirmPassword}
                                    className="btn btn-primary" style={{ width: '100%' }}
                                    data-testid="reset-submit">
                                {loading ? <span className="spinner" /> : <>Setează parola <ArrowRight size={16} /></>}
                            </button>
                            <button type="button" className="btn btn-ghost btn-sm"
                                    style={{ width: '100%', marginTop: 8 }}
                                    onClick={() => { setStep(1); setCode(''); setNewPassword(''); setConfirmPassword(''); setError(''); setInfo(''); }}>
                                ← Alt email
                            </button>
                        </form>
                    </>
                )}

                {step === 3 && (
                    <>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8, justifyContent: 'center' }}>
                            <Shield size={22} color="var(--neon-teal)" />
                            <h1 className="headline" style={{ fontSize: '2rem', margin: 0 }}>Verificare 2FA</h1>
                        </div>
                        <p style={{ color: 'var(--text-muted)', marginBottom: '1.4rem', textAlign: 'center' }}>
                            Parola a fost schimbată. Pentru securitate, introdu codul de 6 cifre din Google Authenticator pentru a finaliza login-ul.
                        </p>
                        <form onSubmit={submit2fa} className="panel" style={{ padding: '1.6rem' }}>
                            <input className="input" inputMode="numeric" maxLength={6} autoFocus required
                                   data-testid="reset-2fa-code"
                                   value={totpCode}
                                   onChange={e => setTotpCode(e.target.value.replace(/\D/g, ''))}
                                   placeholder="000000"
                                   style={{ letterSpacing: '0.45em', textAlign: 'center', fontSize: '1.5rem', marginBottom: '1rem' }} />
                            {error && <div style={{ color: 'var(--neon-magenta)', fontSize: '0.88rem', marginBottom: '0.8rem' }}
                                          data-testid="reset-2fa-error">{error}</div>}
                            <button type="submit" disabled={loading || totpCode.length < 6}
                                    className="btn btn-primary" style={{ width: '100%' }}
                                    data-testid="reset-2fa-submit">
                                {loading ? <span className="spinner" /> : <>Verifică <ArrowRight size={16} /></>}
                            </button>
                        </form>
                    </>
                )}

                <div style={{ textAlign: 'center', marginTop: '1.4rem', color: 'var(--text-muted)' }}>
                    <Link to="/login" style={{ color: 'var(--amber)' }}>← Înapoi la login</Link>
                </div>
            </div>
        </div>
    );
}
